import { z } from "zod";
import { AUTH_API_KEYS } from "@/features/auth/config/authApiCodes";
import {
  authLoginResponseSchema,
  authWith2FactorResponseSchema,
  authResponseSchema,
  refreshTokenResponseSchema,
  authErrorResponseSchema,
  authMissingCellPhoneResponseSchema,
  authCheck2FAResponseSchema,
  authRegisterPhoneResponseSchema,
  loginApprovalPendingSchema,
} from "@/features/auth/schemas/api/auth.schema";
import type { Organization } from "@/features/auth/types/organization.type";
import type { Plan } from "@/features/auth/types/plan.type";
import type { UserResponse } from "@/shared/types/";

export type AuthLoginResponse = z.infer<typeof authLoginResponseSchema>;

export type AuthCheck2FAResponse = z.infer<typeof authCheck2FAResponseSchema>;

export type AuthWith2FactorResponse = z.infer<typeof authWith2FactorResponseSchema>;

export type AuthMissingCellPhoneResponse = z.infer<typeof authMissingCellPhoneResponseSchema>;

export type AuthRegisterPhoneResponse = z.infer<typeof authRegisterPhoneResponseSchema>;

export type RefreshTokenResponse = z.infer<typeof refreshTokenResponseSchema>;

export type AuthErrorResponse = z.infer<typeof authErrorResponseSchema>;

export type AuthResponse = z.infer<typeof authResponseSchema>;

export type LoginApprovalPending = z.infer<typeof loginApprovalPendingSchema>;

// What the browser polls with: the request id and the secret it alone received.
export type LoginApprovalData = {
  number: number
  pollSecret: string
  expiresIn: number
};

export const APP_APPROVAL_CHANNEL = "app_approval";

export const isAuthLoginResponse = (auth: AuthResponse | undefined): auth is AuthLoginResponse => {
  return !!auth && AUTH_API_KEYS.ACCESS_TOKEN in auth;
};

export const isTwoFactorRequired = (auth: AuthResponse | RefreshTokenResponse | undefined): auth is AuthWith2FactorResponse => {
  return !!auth && typeof auth === "object" && AUTH_API_KEYS.TWO_FACTOR_REQUIRED in auth && !!auth.two_factor_required;
};

export const isAuthErrorResponse = (auth: AuthResponse | undefined): auth is AuthErrorResponse => {
  return !!auth && typeof auth === "object" && "error" in auth;
};

export const isMissingCellPhoneNumber = (auth: AuthResponse | undefined): auth is AuthMissingCellPhoneResponse => {
  return isAuthErrorResponse(auth) && auth.error.code === AUTH_API_KEYS.MISSING_CELLPHONE;
};

export const isFirstSignIn = (auth: AuthResponse | undefined): auth is AuthErrorResponse => {
  return isAuthErrorResponse(auth) && auth.error.code === AUTH_API_KEYS.FIRST_SIGN_IN;
};

export type AuthLoginPayload = {
  username: string
  password: string
  hcaptcha: string | null
};

export type AuthCheck2FAPayload = {
  username: string
  password: string
  otp_code: string
  otp_challenge: string
};

export type AuthRegisterPhonePayload = {
  username: string
  password: string
  cell_phone: string
  indicatif: string
  allow_show_cell_phone: boolean
  allow_use_cell_phone: boolean
};

export type AuthResetPasswordPayload = {
  email: string
  hcaptcha: string | null
};

export type AuthResetPasswordResponse = { message: string };

export type AuthSetPasswordPayload = {
  token: string
  password: string
  password_confirmation: string
  scope: "employee"
};

export type AuthSetPasswordResponse = { message: string };

export type AuthResendResetLinkPayload = {
  token: string
  scope: "employee"
};

export type AuthResendResetLinkResponse = { message: string };

export type AuthStorageInfo = {
  accessToken: string
  deviceId: string
};

export type SessionState = {
  isAuthenticating: boolean
  isWaitingForUser: boolean
  isWaitingForPlan: boolean
  isWaitingForOrganization: boolean
  isWaitingForCmsPages: boolean
  isAuthFailed: boolean
  isTwoFactorRequired: boolean
  isCellPhoneRequired: boolean
  isFirstSignInRequired: boolean
  isLegalComplianceRequired: boolean
  isAuthenticated: boolean
  user: UserResponse | null
  plan: Plan | null
  organization: Organization | null
  twoFactorData: {
    otp_challenge: string | null
    channel: string | null
    timestamp: number | null
    approval: LoginApprovalData | null
  }
  error: {
    title: string | null
    description: string | null
  }
};

export type RefreshTokenPayload = {
  scope: typeof AUTH_API_KEYS.SCOPE
  grant_type: typeof AUTH_API_KEYS.GRANT_TYPE_REFRESH_TOKEN
  refresh_token: string
  device_id: string
};

export type ImpersonatePayload = {
  employee_id: string
  organization_id: string
  token: string
};
